import { useEffect, useState } from 'react';
import { api } from '../api/shop';
import { brand } from '../brand/brand';
import type { SupportTicket, SupportTicketSummary } from '../api/types';

/**
 * Support, from the customer's side. SOW §2.6.
 *
 * Replaces a `mailto:` link — which had no record, no status, and no way for
 * anyone to tell whether what they sent had been read.
 *
 * Three states in one component (list, one ticket, new ticket) rather than three
 * routes, because this lives inside an account tab and a route change would lose
 * the tab. The conversation is the important one: it is what makes this feel like
 * being answered rather than shouting into a form.
 */
export function SupportTickets() {
  const [tickets, setTickets] = useState<SupportTicketSummary[] | null>(null);
  const [open, setOpen] = useState<SupportTicket | null>(null);
  const [composing, setComposing] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    api.tickets()
      .then((r) => { if (!cancelled) setTickets(r); })
      .catch(() => { if (!cancelled) setFailed(true); });

    return () => { cancelled = true; };
  }, []);

  async function refresh() {
    try {
      setTickets(await api.tickets());
    } catch {
      // The list is stale, not gone. Leaving what is on screen beats replacing a
      // working list with an error because a background refresh failed.
    }
  }

  if (open !== null) {
    return (
      <TicketThread
        ticket={open}
        onBack={() => { setOpen(null); void refresh(); }}
        onUpdated={setOpen}
      />
    );
  }

  if (composing) {
    return (
      <NewTicket
        onCancel={() => setComposing(false)}
        onCreated={(ticket) => { setComposing(false); setOpen(ticket); void refresh(); }}
      />
    );
  }

  return (
    <div className="stack" style={{ gap: 18 }}>
      <section className="stack" style={{ gap: 10 }}>
        <h2>Get help</h2>
        <p>
          Tell us what has happened and we will reply {brand.supportReplyTime}. You
          can see everything you have raised here, with our answers.
        </p>
        <button
          type="button"
          className="btn"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => setComposing(true)}
        >
          Raise a new ticket
        </button>
      </section>

      {failed && (
        <p className="notice error" role="alert">
          We could not load your tickets just now. Please try again.
        </p>
      )}

      {tickets !== null && tickets.length > 0 && (
        <section className="stack" style={{ gap: 10 }}>
          <h2>Your tickets</h2>
          <ul className="acct-list">
            {tickets.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className="acct-row as-button"
                  onClick={() => { void openTicket(t.id, setOpen); }}
                >
                  <span className="acct-row-main">
                    <span className="acct-row-title">
                      {t.subject}
                      {/* The badge is not the only signal — the status text is
                          below — because colour alone is not information. */}
                      {t.hasUnread && <span className="badge new">New reply</span>}
                    </span>
                    <span className="acct-row-sub">
                      {t.reference} · {statusWord(t.status)}
                      {t.businessName !== null && ` · ${t.businessName}`}
                    </span>
                  </span>
                  <span className="acct-row-side">
                    <span className="acct-row-sub">
                      {t.messageCount === 1 ? '1 message' : `${t.messageCount} messages`}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

async function openTicket(id: string, set: (t: SupportTicket) => void) {
  try {
    set(await api.ticket(id));
  } catch {
    // Opening failed; the list stays. There is nothing useful to say that the
    // customer cannot resolve by tapping again.
  }
}

/* ------------------------------------------------------------------ thread --- */

function TicketThread({
  ticket,
  onBack,
  onUpdated,
}: {
  ticket: SupportTicket;
  onBack: () => void;
  onUpdated: (t: SupportTicket) => void;
}) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const closed = ticket.status === 'closed';

  async function send() {
    if (body.trim() === '') return;

    setBusy(true);
    setNotice(null);

    try {
      onUpdated(await api.replyToTicket(ticket.id, body.trim()));
      setBody('');
    } catch {
      setNotice('We could not send that. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <button type="button" className="btn secondary" style={{ alignSelf: 'flex-start' }} onClick={onBack}>
        Back to your tickets
      </button>

      <div>
        <h2>{ticket.subject}</h2>
        <p className="muted">
          {ticket.reference} · {statusWord(ticket.status)}
          {ticket.businessName !== null && ` · ${ticket.businessName}`}
        </p>
      </div>

      <ul className="thread">
        {ticket.messages.map((m) => (
          <li key={m.id} className={m.author === 'agent' ? 'msg agent' : 'msg mine'}>
            {/*
              "Us" and "You", never a staff member's name. Which agent answered is
              internal and the API deliberately does not send it.
            */}
            <p className="msg-who">{m.author === 'agent' ? brand.name : 'You'}</p>
            <p className="msg-body">{m.body}</p>
            <p className="msg-when">{formatWhen(m.createdAt)}</p>
          </li>
        ))}
      </ul>

      {notice !== null && <p className="notice" role="alert">{notice}</p>}

      {closed ? (
        <p className="hint">
          This ticket is closed. If there is more to say, raise a new one and
          mention {ticket.reference}.
        </p>
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          <label className="field">
            <span className="field-label">Add a reply</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              maxLength={4000}
            />
          </label>
          <button
            type="button"
            className="btn"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => void send()}
            disabled={busy || body.trim() === ''}
          >
            {busy ? 'Sending…' : 'Send reply'}
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- new ticket --- */

const CATEGORIES = [
  { value: 'payment', label: 'A payment' },
  { value: 'voucher', label: 'A voucher' },
  { value: 'redemption', label: 'Spending at a counter' },
  { value: 'account', label: 'My account' },
  { value: 'partner', label: 'A partner business' },
  { value: 'other', label: 'Something else' },
] as const;

function NewTicket({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (t: SupportTicket) => void;
}) {
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<string>('other');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Mirrors the server's own minimum. Enforced there too — this is a courtesy so
  // the customer is not told off after pressing send.
  const ready = subject.trim().length >= 3 && body.trim().length >= 10;

  async function submit() {
    setBusy(true);
    setNotice(null);

    try {
      onCreated(await api.createTicket({
        subject: subject.trim(),
        body: body.trim(),
        category,
      }));
    } catch {
      setNotice('We could not raise that ticket. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <h2>Raise a ticket</h2>

      <label className="field">
        <span className="field-label">What is it about?</span>
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </label>

      <label className="field">
        <span className="field-label">Subject</span>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={140}
        />
      </label>

      <label className="field">
        <span className="field-label">What happened?</span>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={6}
          maxLength={4000}
        />
        <span className="field-hint">
          Dates, amounts and which place it was at all help us sort it faster.
        </span>
      </label>

      {notice !== null && <p className="notice" role="alert">{notice}</p>}

      <div className="stack-h wrap" style={{ gap: 10 }}>
        <button type="button" className="btn" onClick={() => void submit()} disabled={busy || !ready}>
          {busy ? 'Sending…' : 'Send'}
        </button>
        <button type="button" className="btn secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ shared --- */

/** The status in the customer's words. 'pending' means we are waiting on them. */
function statusWord(status: SupportTicketSummary['status']): string {
  switch (status) {
    case 'open': return 'With us';
    case 'pending': return 'Waiting on you';
    case 'resolved': return 'Resolved';
    case 'closed': return 'Closed';
    default: return status;
  }
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
